import {
  showOlbAddIncomeStep,
  showOlbEmploymentDetailsStep,
  showOlbLivingArrangementStep,
} from "./olbStepConditions";
import type { FlowStep, NavigationContext } from "../flowConfig";

function isMembershipSelectionStep(context: NavigationContext) {
  return context.applicationState === "membership_selection";
}

function canShowMembershipVerifyEmailStep(context: NavigationContext) {
  return isMembershipSelectionStep(context) || isMembershipSharePendingStep(context);
}

function isMembershipSharePendingStep(context: NavigationContext) {
  return context.applicationState === "membership_share_pending";
}

function canShowMembershipShareStatusStep(context: NavigationContext) {
  return isMembershipSharePendingStep(context) || Boolean(context.accountApplicationUuid);
}

function canShowCoApplicantEmploymentDetailsStep(context: NavigationContext) {
  return (
    (context.isCoApplicantEnabled ?? false) &&
    context.hasCoApplicant === true &&
    (context.showEmploymentDetailsStep ?? true)
  );
}

export const creditCardFlow: Record<"web" | "olb", FlowStep[]> = {
  web: [
    // Eligibility selection (only shown if institution has eligibility criteria)
    {
      id: "eligibility",
      getPath: ({ applicationUuid }) =>
        applicationUuid
          ? `/credit-card/${applicationUuid}/eligibility`
          : "/credit-card/eligibility",
      getMatchPaths: ({ applicationUuid }) =>
        applicationUuid
          ? ["/credit-card/eligibility", `/credit-card/${applicationUuid}/eligibility`]
          : ["/credit-card/eligibility"],
      condition: (context: NavigationContext) => context.hasEligibilityCriteria === true,
    },

    // MLA covered-borrower question (LNDG-1483 backport; shown when the
    // mla_question additional field is enabled)
    {
      id: "questions",
      getPath: ({ applicationUuid }) =>
        applicationUuid ? `/credit-card/${applicationUuid}/questions` : "/credit-card/questions",
      getMatchPaths: ({ applicationUuid }) =>
        applicationUuid
          ? ["/credit-card/questions", `/credit-card/${applicationUuid}/questions`]
          : ["/credit-card/questions"],
      condition: (context: NavigationContext) => context.hasLendingQuestions === true,
    },

    // Application start
    {
      id: "get-started",
      getPath: ({ applicationUuid }) =>
        applicationUuid
          ? `/credit-card/${applicationUuid}/get-started`
          : "/credit-card/get-started",
      getMatchPaths: ({ applicationUuid }) =>
        applicationUuid
          ? ["/credit-card/get-started", `/credit-card/${applicationUuid}/get-started`]
          : ["/credit-card/get-started"],
      condition: (context: NavigationContext) => context.hasMultipleCreditCardProducts !== true,
    },
    {
      id: "select-card",
      getPath: ({ applicationUuid }) =>
        applicationUuid
          ? `/credit-card/${applicationUuid}/select-card`
          : "/credit-card/select-card",
      getMatchPaths: ({ applicationUuid }) =>
        applicationUuid
          ? ["/credit-card/select-card", `/credit-card/${applicationUuid}/select-card`]
          : ["/credit-card/select-card"],
      condition: (context: NavigationContext) => context.hasMultipleCreditCardProducts === true,
    },

    // Primary applicant information
    {
      id: "phone",
      getPath: ({ applicationUuid }) =>
        applicationUuid ? `/credit-card/${applicationUuid}/phone` : "/credit-card/phone",
      getMatchPaths: ({ applicationUuid }) =>
        applicationUuid
          ? ["/credit-card/phone", `/credit-card/${applicationUuid}/phone`]
          : ["/credit-card/phone"],
      condition: (context: NavigationContext) => context.isPlaidLayerEnabled === true,
    },
    {
      id: "personal-info-review",
      getPath: ({ applicationUuid }) =>
        applicationUuid
          ? `/credit-card/${applicationUuid}/personal-info-review`
          : "/credit-card/personal-info-review",
      getMatchPaths: ({ applicationUuid }) =>
        applicationUuid
          ? [
              "/credit-card/personal-info-review",
              `/credit-card/${applicationUuid}/personal-info-review`,
            ]
          : ["/credit-card/personal-info-review"],
      condition: (context: NavigationContext) =>
        context.isPlaidLayerEnabled === true && context.skippedPlaidPrefill !== true,
    },
    {
      id: "personal-info",
      getPath: ({ applicationUuid }) =>
        applicationUuid
          ? `/credit-card/${applicationUuid}/personal-info`
          : "/credit-card/personal-info",
      getMatchPaths: ({ applicationUuid }) =>
        applicationUuid
          ? ["/credit-card/personal-info", `/credit-card/${applicationUuid}/personal-info`]
          : ["/credit-card/personal-info"],
      condition: (context: NavigationContext) =>
        (context.isPlaidLayerEnabled === true && context.skippedPlaidPrefill === true) ||
        context.isPlaidLayerEnabled !== true,
    },
    {
      id: "address",
      path: "/credit-card/:applicationUuid/address",
      condition: (context: NavigationContext) =>
        (context.isPlaidLayerEnabled === true && context.skippedPlaidPrefill === true) ||
        context.isPlaidLayerEnabled !== true,
    },
    {
      id: "living-arrangement",
      path: "/credit-card/:applicationUuid/living-arrangement",
      condition: (context: NavigationContext) => context.showLivingArrangementStep ?? true,
    },
    { id: "add-income", path: "/credit-card/:applicationUuid/add-income" },
    {
      id: "employment-details",
      path: "/credit-card/:applicationUuid/employment-details",
      condition: (context: NavigationContext) => context.showEmploymentDetailsStep ?? true,
    },

    // Co-applicant flow (conditional)
    {
      id: "co-applicant-ask",
      path: "/credit-card/:applicationUuid/co-applicant/ask",
      condition: (context: NavigationContext) => context.isCoApplicantEnabled ?? false,
    },
    {
      id: "co-applicant-details",
      path: "/credit-card/:applicationUuid/co-applicant/details",
      condition: (context: NavigationContext) =>
        (context.isCoApplicantEnabled ?? false) && context.hasCoApplicant === true,
    },
    {
      id: "co-applicant-address-ask",
      path: "/credit-card/:applicationUuid/co-applicant/address-ask",
      condition: (context: NavigationContext) =>
        (context.isCoApplicantEnabled ?? false) && context.hasCoApplicant === true,
    },
    {
      id: "co-applicant-address",
      path: "/credit-card/:applicationUuid/co-applicant/address",
      condition: (context: NavigationContext) =>
        (context.isCoApplicantEnabled ?? false) &&
        context.hasCoApplicant === true &&
        context.hasSeparateCoApplicantAddress === true,
    },
    {
      id: "co-applicant-income",
      path: "/credit-card/:applicationUuid/co-applicant/income",
      condition: (context: NavigationContext) =>
        (context.isCoApplicantEnabled ?? false) && context.hasCoApplicant === true,
    },
    {
      id: "co-applicant-employment-details",
      path: "/credit-card/:applicationUuid/co-applicant/employment-details",
      condition: canShowCoApplicantEmploymentDetailsStep,
    },
    // Final steps
    { id: "disclosures", path: "/credit-card/:applicationUuid/disclosures" },
    { id: "credit-pull", path: "/credit-card/:applicationUuid/credit-pull" },
    { id: "offers", path: "/credit-card/:applicationUuid/offers" },
    {
      id: "membership-share-select",
      path: "/credit-card/:applicationUuid/membership-share/select",
      condition: isMembershipSelectionStep,
    },
    {
      id: "membership-share-use-different-email",
      path: "/credit-card/:applicationUuid/membership-share/use-different-email",
      condition: canShowMembershipVerifyEmailStep,
    },
    {
      id: "membership-share-verify-email",
      path: "/credit-card/:applicationUuid/membership-share/verify-email",
      condition: canShowMembershipVerifyEmailStep,
    },
    {
      id: "membership-share-activate",
      path: "/credit-card/:applicationUuid/membership-share/activate",
      condition: isMembershipSharePendingStep,
    },
    {
      id: "membership-share-disclosures",
      path: "/credit-card/:applicationUuid/membership-share/disclosures",
      condition: isMembershipSharePendingStep,
    },
    {
      id: "membership-share-processing",
      path: "/credit-card/:applicationUuid/membership-share/processing",
      condition: isMembershipSharePendingStep,
    },
    {
      id: "membership-share-documents-requested",
      path: "/credit-card/:applicationUuid/membership-share/documents-requested",
      condition: isMembershipSharePendingStep,
    },
    {
      id: "membership-share-submitting-documents",
      path: "/credit-card/:applicationUuid/membership-share/submitting-documents",
      condition: isMembershipSharePendingStep,
    },
    {
      id: "membership-share-status",
      path: "/credit-card/:applicationUuid/membership-share/status",
      condition: canShowMembershipShareStatusStep,
    },
    { id: "submitted", path: "/credit-card/:applicationUuid/submitted" },
  ],
  olb: [
    // Application start
    { id: "confirm-info", path: "/olb-credit-card/:applicationUuid/confirm-info" },
    {
      id: "select-card",
      path: "/olb-credit-card/:applicationUuid/select-card",
      condition: (context: NavigationContext) =>
        context.hasMultipleCreditCardProducts === true &&
        context.hasSelectedCreditCardProduct !== true,
    },

    // Primary applicant information
    {
      id: "living-arrangement",
      path: "/olb-credit-card/:applicationUuid/living-arrangement",
      condition: showOlbLivingArrangementStep,
    },
    {
      id: "add-income",
      path: "/olb-credit-card/:applicationUuid/add-income",
      condition: showOlbAddIncomeStep,
    },
    {
      id: "employment-details",
      path: "/olb-credit-card/:applicationUuid/employment-details",
      condition: showOlbEmploymentDetailsStep,
    },

    // Co-applicant flow (conditional)
    {
      id: "co-applicant-ask",
      path: "/olb-credit-card/:applicationUuid/co-applicant/ask",
      condition: (context: NavigationContext) =>
        (context.isCoApplicantEnabled ?? false) && context.hasCoApplicant !== false,
    },
    {
      id: "co-applicant-details",
      path: "/olb-credit-card/:applicationUuid/co-applicant/details",
      condition: (context: NavigationContext) =>
        (context.isCoApplicantEnabled ?? false) && context.hasCoApplicant === true,
    },
    {
      id: "co-applicant-address",
      path: "/olb-credit-card/:applicationUuid/co-applicant/address",
      condition: (context: NavigationContext) =>
        (context.isCoApplicantEnabled ?? false) && context.hasCoApplicant === true,
    },
    {
      id: "co-applicant-income",
      path: "/olb-credit-card/:applicationUuid/co-applicant/income",
      condition: (context: NavigationContext) =>
        (context.isCoApplicantEnabled ?? false) && context.hasCoApplicant === true,
    },
    {
      id: "co-applicant-employment-details",
      path: "/olb-credit-card/:applicationUuid/co-applicant/employment-details",
      condition: canShowCoApplicantEmploymentDetailsStep,
    },

    // Final steps
    { id: "disclosures", path: "/olb-credit-card/:applicationUuid/disclosures" },
    { id: "credit-pull", path: "/olb-credit-card/:applicationUuid/credit-pull" },
    { id: "offers", path: "/olb-credit-card/:applicationUuid/offers" },
    { id: "submitted", path: "/olb-credit-card/:applicationUuid/submitted" },
  ],
};
